import type { LanguageEnum } from 'js/src/client';
import type {
    SerializedTranscript,
    TranscriptTranslations,
} from 'js/src/data/transcript/transcript-types';
import {
    createLexicalEditor,
    getNextTranslationEditorName,
    TRANSCRIPT_EDITOR_CONFIG,
} from 'js/src/data/transcript/transcript-utils';
import type { TranscriptTranslationsType } from 'js/src/data/translated-editors/translated-editors-types';

type TranslationsEditorReducerType = {
    sceneGroupId: string;
    translations: TranscriptTranslationsType;
}[];

export const reduceTranscriptsToTranslationEditorsMap = (
    transcripts: Record<string, SerializedTranscript>,
) => {
    const sceneGroupIdsWithTranslations = Object.keys(transcripts).filter((sceneGroupId) => {
        const hasEditorState = !!transcripts[sceneGroupId].editorState;
        const hasValidTranslations =
            transcripts[sceneGroupId]?.translations &&
            Object.keys(transcripts[sceneGroupId].translations).length > 0;

        return hasEditorState && hasValidTranslations;
    });

    const sceneGroupToTranslationEditorsMaps = sceneGroupIdsWithTranslations.reduce(
        (allTranslations: TranslationsEditorReducerType, sceneGroupId: string) => {
            const transcript = transcripts[sceneGroupId];
            if (!transcript.translations) {
                return allTranslations;
            }
            const translationsInSceneGroup = reduceTranslationsToEditorsMap(
                sceneGroupId,
                transcript.translations,
            );
            if (translationsInSceneGroup) {
                allTranslations.push(translationsInSceneGroup);
            }
            return allTranslations;
        },
        [],
    );

    return sceneGroupToTranslationEditorsMaps;
};

export const reduceTranslationsToEditorsMap = (
    sceneGroupId: string,
    translations: TranscriptTranslations,
) => {
    const languageKeys = Object.keys(translations) as LanguageEnum[];
    if (languageKeys.length === 0) {
        return;
    }

    const translationEditorMap = languageKeys.reduce<
        Partial<Record<LanguageEnum, TranscriptTranslationsType[LanguageEnum]>>
    >((currMap, langCode) => {
        const editor = createLexicalEditor(
            TRANSCRIPT_EDITOR_CONFIG,
            getNextTranslationEditorName(),
        );
        const parsedEditorState = editor.parseEditorState(
            JSON.stringify(translations[langCode]?.editorState),
        );
        editor.setEditorState(parsedEditorState);

        return {
            ...currMap,
            [langCode]: {
                editor,
                lexicalControlsItsOwnState: false,
            },
        };
    }, {});

    return {
        sceneGroupId: sceneGroupId,
        translations: translationEditorMap as TranscriptTranslationsType,
    };
};
